/**
 * Stage 091 — Advisory Validation Adapter & Freshness State Machine
 * 
 * Strict architectural boundaries:
 * 1. Epoch Milliseconds Time Model: time = Date.parse(open_time_utc)
 * 2. Pure Decimal Text Boundary: formatFinancialPrice() for presentation;
 *    toChartFloat() ONLY for disposable Canvas2D rendering leaf coordinates.
 * 3. Exact TD Anchor Match: strictly kline.close_time_utc === blueprint.close_time_utc.
 * 4. Truthful 5-State Machine with Ordered Precedence:
 *    Priority 1: DISCONNECTED (quote_age_seconds > 30, fetch fail, status == disconnected)
 *    Priority 2: LOADING (loading active)
 *    Priority 3: EMPTY (candles length 0, status == empty)
 *    Priority 4: STALE (derivedIsExpired, candle age > 2*duration, causal violation, anchor mismatch)
 *    Priority 5: FRESH (fully aligned, fresh quote and tactics, within 2*duration)
 *    Fail-closed fallback: STALE
 */

import { CanonicalCandleV1, TradeAdvisorySymbolBlueprintV1, CadenceStatusV1 } from '../../api/client';
import { formatFinancialPrice } from '../../shared/domain/formatters';

export type TimeframeId = '5m' | '15m' | '30m' | '1h' | '4h' | '1d';

export type FreshnessState = 'DISCONNECTED' | 'LOADING' | 'EMPTY' | 'STALE' | 'FRESH';

export const CADENCE_DURATIONS_SECONDS: Record<TimeframeId, number> = {
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '4h': 14400,
  '1d': 86400,
};

export interface ChartOHLCVItem {
  time: number; // epoch milliseconds: Date.parse(open_time_utc)
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  rawCloseTimeUtc: string;
}

export interface ChartReferenceLine {
  id: string;
  price: number;
  rawPrice: string;
  label: string;
  color: string;
  lineStyle: 'solid' | 'dashed' | 'dotted';
}

export interface ChartBand {
  id: string;
  priceLow: number;
  priceHigh: number;
  rawPriceLow: string;
  rawPriceHigh: string;
  label: string;
  color: string;
}

export interface ChartTDMarker {
  time: number | null; // epoch ms of open_time_utc of matched candle
  count: number | null;
  direction: 'BUY' | 'SELL' | null;
  isAligned: boolean;
  message?: string;
}

export interface AdaptedValidationData {
  chartCandles: ChartOHLCVItem[];
  referenceLines: ChartReferenceLine[];
  entryBand: ChartBand | null;
  tdMarker: ChartTDMarker;
  freshnessState: FreshnessState;
  stateReason: string;
  isExpired: boolean;
}

/**
 * Disposable float converter for leaf canvas rendering ONLY.
 * Never feed back into financial business domain or calculations.
 */
export function toChartFloat(val: string | null | undefined): number {
  if (val === null || val === undefined || val === '') return NaN;
  const num = Number(val);
  return Number.isFinite(num) ? num : NaN;
}

export { formatFinancialPrice };

/**
 * Safely extracts uppercase ticker symbol from string or CanonicalInstrumentKeyV1 object.
 */
export function extractCanonicalSymbol(instrument: unknown): string {
  if (!instrument) return '';
  if (typeof instrument === 'string') return instrument.trim().toUpperCase();
  if (typeof instrument === 'object' && instrument !== null && 'symbol' in instrument) {
    const sym = (instrument as { symbol?: unknown }).symbol;
    if (typeof sym === 'string') return sym.trim().toUpperCase();
  }
  return '';
}

/**
 * Derives whether a blueprint's tactical window has expired.
 * Strict equality and invalidation semantics matching Gate 00 R5.
 */
export function derivedIsExpired(
  symbol: { tactics_valid_until_utc?: string | null } | null | undefined,
  nowMs: number,
  cadence?: { is_expired?: boolean | null } | null
): boolean {
  const raw = symbol?.tactics_valid_until_utc;
  const endMs = typeof raw === 'string' && raw.length ? Date.parse(raw) : NaN;
  return !Number.isFinite(nowMs) || !Number.isFinite(endMs) || nowMs >= endMs || cadence?.is_expired === true;
}

/**
 * Evaluates dual-source freshness with strict ordered precedence.
 */
export function evaluateDualSourceFreshness(params: {
  candles: CanonicalCandleV1[];
  blueprint: TradeAdvisorySymbolBlueprintV1 | null | undefined;
  timeframe: TimeframeId;
  nowMs: number;
  isLoading: boolean;
  hasNetworkError: boolean;
  cadenceStatus?: CadenceStatusV1 | null;
}): { state: FreshnessState; reason: string } {
  const {
    candles,
    blueprint,
    timeframe,
    nowMs,
    isLoading,
    hasNetworkError,
    cadenceStatus,
  } = params;

  // 1. Priority 1: DISCONNECTED
  if (hasNetworkError) {
    return { state: 'DISCONNECTED', reason: 'K线接口网络异常或鉴权失败' };
  }
  if (blueprint?.status === 'disconnected') {
    return { state: 'DISCONNECTED', reason: '决策蓝图处于离线断开状态' };
  }
  if (
    blueprint?.quote_age_seconds !== undefined &&
    blueprint?.quote_age_seconds !== null &&
    blueprint.quote_age_seconds > 30
  ) {
    return {
      state: 'DISCONNECTED',
      reason: `标的报价严重滞后 (${blueprint.quote_age_seconds}s > 30s 阈值)`,
    };
  }

  // 2. Priority 2: LOADING
  if (isLoading || blueprint?.status === 'loading') {
    return { state: 'LOADING', reason: '正在加载最新数据' };
  }

  // 3. Priority 3: EMPTY
  if (!candles || candles.length === 0 || blueprint?.status === 'empty') {
    return { state: 'EMPTY', reason: '未检索到有效K线或蓝图数据' };
  }

  // Check instrument/symbol stream alignment between candles and blueprint
  if (candles.length > 0 && candles[0].instrument && blueprint?.symbol) {
    const candleSymbol = extractCanonicalSymbol(candles[0].instrument);
    const blueprintSymbol = blueprint.symbol.trim().toUpperCase();
    if (!candleSymbol || candleSymbol !== blueprintSymbol) {
      return {
        state: 'STALE',
        reason: `标的符号不一致: K线(${candleSymbol || '未知'}) vs 蓝图(${blueprint.symbol})`,
      };
    }
  }

  // 4. Priority 4: STALE
  if (blueprint?.status === 'stale') {
    return { state: 'STALE', reason: '父级决策蓝图已被标为陈旧' };
  }

  const isExpired = derivedIsExpired(blueprint, nowMs, cadenceStatus);
  if (isExpired) {
    return { state: 'STALE', reason: '蓝图战术有效期已过 (Tactics Expired)' };
  }

  // Check blueprint close_time anchor against candle stream
  if (blueprint?.close_time_utc) {
    const hasMatchingCandle = candles.some(c => c.close_time_utc === blueprint.close_time_utc);
    if (!hasMatchingCandle) {
      return {
        state: 'STALE',
        reason: `蓝图决策锚点时间未与当前K线序列对齐 (${blueprint.close_time_utc})`,
      };
    }
  }

  const latestCandle = candles[candles.length - 1];
  const latestCloseMs = Date.parse(latestCandle.close_time_utc);
  if (!Number.isFinite(latestCloseMs)) {
    return { state: 'STALE', reason: 'K线收盘时间戳解析异常' };
  }

  // Causal lag check: W >= 3s (close_time_utc <= now - 3s)
  if (latestCloseMs > nowMs - 3000) {
    return {
      state: 'STALE',
      reason: '最新K线收盘时间违背因果时延守卫 (W >= 3s)',
    };
  }

  // Candle age staleness check: (now - latestClose) > 2 * CadenceDuration
  const durationSec = CADENCE_DURATIONS_SECONDS[timeframe] || 3600;
  const maxLagMs = 2 * durationSec * 1000;
  if (nowMs - latestCloseMs > maxLagMs) {
    return {
      state: 'STALE',
      reason: `K线数据断流超期 (超过2个周期: ${(nowMs - latestCloseMs) / 1000}s > ${2 * durationSec}s)`,
    };
  }

  // 5. Priority 5: FRESH
  if (blueprint?.status === 'fresh' && !isExpired) {
    return { state: 'FRESH', reason: '数据实时新鲜且时效完全对齐' };
  }

  // Fail-Closed default
  return { state: 'STALE', reason: '状态未达全绿标准，安全降级为陈旧' };
}

/**
 * Matches discrete DeMark TD9 setup strictly against candle close_time_utc.
 */
export function matchTdAnchor(
  candles: CanonicalCandleV1[],
  blueprint: TradeAdvisorySymbolBlueprintV1 | null | undefined
): ChartTDMarker {
  if (!blueprint || !blueprint.close_time_utc) {
    return { time: null, count: null, direction: null, isAligned: false, message: '无决策锚点时间' };
  }

  const targetCloseUtc = blueprint.close_time_utc;
  const matchedCandle = candles.find(c => c.close_time_utc === targetCloseUtc);

  if (!matchedCandle) {
    return {
      time: null,
      count: null,
      direction: null,
      isAligned: false,
      message: `TD锚点未对齐 (${targetCloseUtc})`,
    };
  }

  const openMs = Date.parse(matchedCandle.open_time_utc);
  const count = blueprint.td_setup_count ?? null;
  const direction = blueprint.td_setup_direction ?? null;

  if (count !== null && count >= 6) {
    return {
      time: openMs,
      count,
      direction,
      isAligned: true,
      message: `TD9 对齐: ${direction === 'BUY' ? '买' : '卖'} ${count}`,
    };
  }

  return {
    time: openMs,
    count,
    direction,
    isAligned: true,
    message: count ? `TD计数 ${count}` : 'TD正常',
  };
}

/**
 * Maps CanonicalCandleV1 array to ChartOHLCVItem array with epoch ms.
 */
export function mapCanonicalCandlesToChart(candles: CanonicalCandleV1[]): ChartOHLCVItem[] {
  return (candles || []).map(c => {
    const timeMs = Date.parse(c.open_time_utc);
    return {
      time: timeMs,
      open: toChartFloat(c.open),
      high: toChartFloat(c.high),
      low: toChartFloat(c.low),
      close: toChartFloat(c.close),
      volume: toChartFloat(c.volume),
      rawCloseTimeUtc: c.close_time_utc,
    };
  });
}

/**
 * Extracts trade geometry reference lines and entry zone band.
 */
export function extractTradeGeometry(
  blueprint: TradeAdvisorySymbolBlueprintV1 | null | undefined
): {
  referenceLines: ChartReferenceLine[];
  entryBand: ChartBand | null;
} {
  const referenceLines: ChartReferenceLine[] = [];
  let entryBand: ChartBand | null = null;

  if (!blueprint) {
    return { referenceLines, entryBand };
  }

  // 1. Entry zone band (entry_low ~ entry_high)
  if (blueprint.entry_low && blueprint.entry_high) {
    const low = toChartFloat(blueprint.entry_low);
    const high = toChartFloat(blueprint.entry_high);
    if (Number.isFinite(low) && Number.isFinite(high) && low > 0 && high > 0) {
      entryBand = {
        id: 'band-entry-zone',
        priceLow: Math.min(low, high),
        priceHigh: Math.max(low, high),
        rawPriceLow: blueprint.entry_low,
        rawPriceHigh: blueprint.entry_high,
        label: '战术入场带 (ENTRY ZONE)',
        color: 'var(--color-accent-subtle, rgba(46, 117, 89, 0.25))',
      };
    }
  }

  // 2. Invalidation Stop
  if (blueprint.invalidation_stop) {
    const stopPrice = toChartFloat(blueprint.invalidation_stop);
    if (Number.isFinite(stopPrice) && stopPrice > 0) {
      referenceLines.push({
        id: 'line-stop-loss',
        price: stopPrice,
        rawPrice: blueprint.invalidation_stop,
        label: `止损位 (STOP): ${formatFinancialPrice(blueprint.invalidation_stop)}`,
        color: 'var(--color-down, #c94a4a)',
        lineStyle: 'dashed',
      });
    }
  }

  // 3. Target Take Profit
  if (blueprint.target_take_profit) {
    const targetPrice = toChartFloat(blueprint.target_take_profit);
    if (Number.isFinite(targetPrice) && targetPrice > 0) {
      referenceLines.push({
        id: 'line-take-profit',
        price: targetPrice,
        rawPrice: blueprint.target_take_profit,
        label: `目标止盈 (TARGET): ${formatFinancialPrice(blueprint.target_take_profit)}`,
        color: 'var(--color-up, #2e7559)',
        lineStyle: 'dashed',
      });
    }
  }

  // 4. Trailing Breakeven Trigger (Optional)
  if (blueprint.trailing_breakeven_trigger) {
    const bePrice = toChartFloat(blueprint.trailing_breakeven_trigger);
    if (Number.isFinite(bePrice) && bePrice > 0) {
      referenceLines.push({
        id: 'line-breakeven',
        price: bePrice,
        rawPrice: blueprint.trailing_breakeven_trigger,
        label: `保本保护 (BE): ${formatFinancialPrice(blueprint.trailing_breakeven_trigger)}`,
        color: 'var(--color-info, #4a90e2)',
        lineStyle: 'dotted',
      });
    }
  }

  return { referenceLines, entryBand };
}

/**
 * Main adapter function synthesizing all streams into Chart-ready structures.
 */
export function adaptAdvisoryValidationData(params: {
  candles: CanonicalCandleV1[];
  blueprint: TradeAdvisorySymbolBlueprintV1 | null | undefined;
  timeframe: TimeframeId;
  nowMs?: number;
  isLoading?: boolean;
  hasNetworkError?: boolean;
  cadenceStatus?: CadenceStatusV1 | null;
}): AdaptedValidationData {
  const nowMs = params.nowMs ?? Date.now();
  const isLoading = params.isLoading ?? false;
  const hasNetworkError = params.hasNetworkError ?? false;

  const { state: freshnessState, reason: stateReason } = evaluateDualSourceFreshness({
    candles: params.candles,
    blueprint: params.blueprint,
    timeframe: params.timeframe,
    nowMs,
    isLoading,
    hasNetworkError,
    cadenceStatus: params.cadenceStatus,
  });

  const chartCandles = mapCanonicalCandlesToChart(params.candles);
  const tdMarker = matchTdAnchor(params.candles, params.blueprint);
  const { referenceLines, entryBand } = extractTradeGeometry(params.blueprint);
  const isExpired = derivedIsExpired(params.blueprint, nowMs, params.cadenceStatus);

  return {
    chartCandles,
    referenceLines,
    entryBand,
    tdMarker,
    freshnessState,
    stateReason,
    isExpired,
  };
}
